/**
 * Build a Z-orbit geometry with a world-X-stabilized detector.
 *
 * Source and detector centres follow the inclined orbit used by the
 * inclined geometry. Unlike that rigid rotation, detector U is obtained by
 * projecting world +X onto the detector plane. This removes detector roll:
 * the edge that starts on the right remains the right edge throughout the
 * orbit. V completes the orthonormal detector basis.
 *
 * @param {Object} p - Geometry parameters
 * @param {number} p.numOfImgs - Number of projection angles
 * @param {number[]} p.angles - Array of projection angles in degrees
 * @param {number} p.tiltX - Tilt angle around X axis in degrees
 * @param {number} p.tiltY - Tilt angle around Y axis in degrees
 * @param {number} p.sod - Source-to-object distance
 * @param {number} p.sdd - Source-to-detector distance
 * @param {number} p.detPitch - Detector pixel pitch
 * @param {number} [p.offsetU=0] - Detector U offset
 * @param {number} [p.offsetV=0] - Detector V offset
 * @returns {number[][]} Array of rows [Sx Sy Sz Dx Dy Dz Ux Uy Uz Vx Vy Vz]
 */
export function computeGimbalLockGeom(p) {
  const deg2rad = (d) => (d * Math.PI) / 180;

  const tx = deg2rad(p.tiltX);
  const ty = deg2rad(p.tiltY);

  const sx = Math.sin(tx);
  const cx = Math.cos(tx);
  const sy = Math.sin(ty);
  const cy = Math.cos(ty);

  const offsetU = p.offsetU || 0;
  const offsetV = p.offsetV || 0;

  // Inclined detector normal at angle zero; its orbit is around world Z.
  const n0 = [cx * sy, -sx, cx * cy];

  const vecs = [];

  for (let i = 0; i < p.numOfImgs; i++) {
    const phi = -deg2rad(p.angles[i]);
    const sp = Math.sin(phi);
    const cp = Math.cos(phi);

    const normal = [
      cp * n0[0] - sp * n0[1],
      sp * n0[0] + cp * n0[1],
      n0[2],
    ];

    // Project fixed world-right into the detector plane. This no-roll
    // constraint keeps the same physical detector edge on the right.
    const worldRight = [1.0, 0.0, 0.0];
    const dot = normal[0] * worldRight[0] + normal[1] * worldRight[1] + normal[2] * worldRight[2];
    const u = [
      worldRight[0] - normal[0] * dot,
      worldRight[1] - normal[1] * dot,
      worldRight[2] - normal[2] * dot,
    ];
    const uNorm = Math.sqrt(u[0] * u[0] + u[1] * u[1] + u[2] * u[2]);
    if (uNorm < 1e-12) {
      throw new Error('Gimbal U axis is singular when detector normal aligns with world X');
    }
    u[0] /= uNorm;
    u[1] /= uNorm;
    u[2] /= uNorm;

    // v = u cross normal
    const v = [
      u[1] * normal[2] - u[2] * normal[1],
      u[2] * normal[0] - u[0] * normal[2],
      u[0] * normal[1] - u[1] * normal[0],
    ];

    const row = new Array(12).fill(0);

    row[0] = -p.sod * normal[0];
    row[1] = -p.sod * normal[1];
    row[2] = -p.sod * normal[2];

    row[3] = (p.sdd - p.sod) * normal[0];
    row[4] = (p.sdd - p.sod) * normal[1];
    row[5] = (p.sdd - p.sod) * normal[2];

    row[6] = p.detPitch * u[0];
    row[7] = p.detPitch * u[1];
    row[8] = p.detPitch * u[2];

    row[9] = p.detPitch * v[0];
    row[10] = p.detPitch * v[1];
    row[11] = p.detPitch * v[2];

    row[3] += offsetU * row[6] + offsetV * row[9];
    row[4] += offsetU * row[7] + offsetV * row[10];
    row[5] += offsetU * row[8] + offsetV * row[11];

    vecs.push(row);
  }

  return vecs;
}

export default computeGimbalLockGeom;
